import '@/lib/dayjsPlugins';
import SnackbarProvider, {
  SnackbarUtilsConfigurator,
} from '@ahamove/web-components/lib/SnackbarProvider';
import ThemeProvider from '@ahamove/web-components/lib/ThemeProvider';
import type { EmotionCache } from '@emotion/react';
import { CacheProvider } from '@emotion/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { detectPlatform, loadJS } from './lib/common';
import { EV_TRACK_KEY } from './lib/constants';
import { createEmotionCache } from './lib/emotion';
import { trackEvent } from './lib/eventTracking';
import { isServer } from './lib/isServer';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './modules/auth/AuthProvider';
import { NewBookingProvider } from './modules/new/booking/NewBookingProvider';
import useIsMobile from './shared-hooks/useIsMobile';

type Props = {
  children: ReactNode;
  emotionCache?: EmotionCache;
};
// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

if (!isServer) {
  const user_agent = navigator.userAgent;
  const platform = detectPlatform(user_agent);
  trackEvent(EV_TRACK_KEY.ev_website_open, {
    user_agent,
    platform,
  });
}

export function AppProviders({
  children,
  emotionCache = clientSideEmotionCache,
}: Props) {
  // This ensures that data is not shared
  // between different users and requests
  const [queryClientState] = useState(() => queryClient);

  React.useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_ERUDA === 'true') {
      const src = `//cdn.jsdelivr.net/npm/eruda`;
      loadJS(src).then(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).eruda && (window as any).eruda._isInit) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).eruda.init();
      });
    }
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).eruda &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).eruda._isInit &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).eruda.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider mode="light" type="ahamove">
      <SnackbarProvider
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        autoHideDuration={3000}
        preventDuplicate
      >
        <SnackbarUtilsConfigurator />
        <CacheProvider value={emotionCache}>
          {/* Mui CssBaseline disabled in this example as tailwind provides its own */}
          {/* <CssBaseline /> */}
          <QueryClientProvider client={queryClientState}>
            <AuthProvider>
              <NewBookingProvider>{children}</NewBookingProvider>
            </AuthProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </CacheProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}
